import React, { useEffect, useRef, useState } from "react";
import {
    Row,
    Col,
    Card,
    Button,
    notification,
    Form,
    Select,
    Input,
} from "antd";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { baseUrl } from "../../config";

const { Item } = Form;
const { Option } = Select;

function CategoryForm() {
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef(null);
    const imageInputRef = useRef(null);
    const { blogId } = useParams(); // Extract reportElementId from URL
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [isUpdateMode, setIsUpdateMode] = useState(false);
    const [bannerImage, setBannerImage] = useState('');
    const [selectedPage, setSelectedPage] = useState('');
    const [blogImage, setBlogImage] = useState();
    const [selectedBannerImage, setSelectedBannerImage] = useState('')
    const [categoryDetails, setCategoryDetails] = useState({});

    useEffect(() => {
        // Check if reportElementId exists to determine if it's an update mode
        if (blogId) {
            setIsUpdateMode(true);
            fetchDetails();
        }
    }, [blogId]);

    const fetchDetails = async () => {
        try {
            const response = await axios.get(`${baseUrl}/api/category/${blogId}`);
            if (response.status === 200) {
                setSelectedPage(response.data.result.page);
                setCategoryDetails(response?.data?.result);
                // setSelectedBannerImage(response?.data?.result?.image || null);
                // setBlogImage(response?.data?.result?.banner || null);
                form.setFieldsValue(response.data.result); // Populate form fields with fetched data
            } else {
                notification.info({
                    message: 'Info',
                    description: response.data.message,
                    placement: 'topRight'
                });
            }
        } catch (error) {
            console.error("API error:", error);
            notification.info({
                message: 'Info',
                description: error.response?.data?.message,
                placement: 'topRight'
            });
        }
    };

    const onFinish = async (values) => {
        try {
            let formData = new FormData();
            for (let key in values) {
                formData.append(key, values[key]);
            }

            if (bannerImage) {
                formData.append('file', bannerImage);
            }
            if (blogImage) {
                formData.append('banner', blogImage);
            }

            if (isUpdateMode) {
                formData.append('id', blogId)
                const response = await axios.patch(`${baseUrl}/api/category`, formData, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`, // Include access token in headers
                    },
                });
                if (response.status === 200) {
                    notification.success({
                        message: 'Success',
                        description: 'Category updated successfully!',
                        placement: 'topRight'
                    });
                    navigate('/category');
                } else {
                    notification.info({
                        message: 'Info',
                        description: response.data.message,
                        placement: 'topRight'
                    });
                }
            } else {
                const response = await axios.post(`${baseUrl}/api/category`, formData, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('accessToken')}`, // Include access token in headers
                    },
                });
                if (response.status === 200) {
                    notification.success({
                        message: 'Success',
                        description: 'Category added successfully!',
                        placement: 'topRight'
                    });
                    form.resetFields();
                    navigate('/category');
                } else {
                    notification.info({
                        message: 'Info',
                        description: response.data.message,
                        placement: 'topRight'
                    });
                }
            }
        } catch (error) {
            console.error("API error:", error);
            notification.info({
                message: 'Info',
                description: error.response?.data?.message,
                placement: 'topRight'
            });
        }
    };

    function bannerImageSelect(brandLogo) {
        setBannerImage(brandLogo);
    }

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.type === "image/svg+xml") {
                bannerImageSelect(file); // valid svg
            } else {
                alert("Only SVG files are allowed!");
                if (fileInputRef.current) {
                    fileInputRef.current.value = ""; // ✅ reset safely
                }
            }
        }
    };

    const handleBannerImageChange = async (e) => {
        setLoading(true);
        const file = e.target.files[0];
        if (!file) return;

        // Validate type
        const allowedTypes = ["image/png", "image/jpeg"];

        if (!allowedTypes.includes(file.type)) {
            alert("Only JPG and PNG files are allowed!");

            if (imageInputRef.current) {
                imageInputRef.current.value = "";
            }

            return;
        }

        try {
            const formData = new FormData();
            formData.append("file", file); // MUST match backend field name

            const response = await axios.post(
                `${baseUrl}/api/imageUpload`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (response.status === 200) {
                setBlogImage(response.data.result.banner);
                setLoading(false);
            }
        } catch (error) {
            console.error("Upload failed:", error);
            setBlogImage('');

            if (imageInputRef.current) {
                imageInputRef.current.value = "";
            }
        }
    };


    return (
        <div className="tabled">
            <Row gutter={[24, 0]}>
                <Col xs="24" xl={24}>
                    <Card
                        bordered={false}
                        className="criclebox tablespace mb-24"
                        title={isUpdateMode ? "Update Category" : "Add Category"}
                    >
                        <Form style={{ "padding": "20px" }} form={form} onFinish={onFinish} layout="vertical">
                            <Row gutter={[16, 16]}>
                                <Col xs={24} sm={12} lg={12}>
                                    <Item
                                        label="Title"
                                        name="title"
                                        rules={[{ required: true, message: 'Please enter title.' }]}
                                    >
                                        <Input placeholder="Please enter title" />
                                    </Item>
                                </Col>
                                <Col xs={24} sm={12} lg={12}>
                                    <Item label="Image/Icon">
                                        <Input
                                            type="file"
                                            accept=".svg,image/svg+xml"
                                            ref={fileInputRef}
                                            onChange={handleFileChange}
                                        />
                                    </Item>

                                    {(categoryDetails.image) && (
                                        <img
                                            width={100}
                                            height={100}
                                            src={baseUrl + '/' + categoryDetails?.image}
                                            alt={'icon'}
                                        />
                                    )}
                                </Col>

                                <Col xs={24} sm={12} lg={12}>
                                    <Item
                                        label="Status"
                                        name="enabled"
                                        rules={[{ required: true, message: 'Please select status.' }]}
                                    >
                                        <Select placeholder="Please select status">
                                            <Option value={true}>Active</Option>
                                            <Option value={false}>Inactive</Option>
                                        </Select>
                                    </Item>
                                </Col>

                                <Col xs={24} sm={12} lg={12}>
                                    <Item label="Banner/Image">
                                        <Input
                                            type="file"
                                            accept="image/png,image/jpeg"
                                            ref={fileInputRef}
                                            onChange={handleBannerImageChange}
                                        />

                                    </Item>

                                    {(blogImage) ?

                                        <img
                                            width={100}
                                            height={100}
                                            src={baseUrl + '/' + blogImage}
                                            alt={"banner"}
                                        />
                                        :
                                        (categoryDetails?.banner) && <>
                                            <img
                                                width={100}
                                                height={100}
                                                src={baseUrl + '/' + categoryDetails?.banner}
                                                alt={"banner"}
                                            />

                                        </>

                                    }
                                </Col>

                            </Row>
                            <Row>
                                <Col xs={24} sm={12} lg={8}>
                                    <Item>
                                        {loading ?
                                            <Button type="secondary" htmlType="button">
                                                Loading...
                                            </Button>
                                            :
                                            <Button type="primary" htmlType="submit">
                                                {isUpdateMode ? "Update" : "Add"}
                                            </Button>
                                        }
                                    </Item>
                                </Col>
                            </Row>
                        </Form>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export default CategoryForm;
